import { MIN_DUR, TOTAL_S, SLOT_H, START_H, weekKey, getMonday, CAL_VIEW_KEY, sanitizeCalView, toast } from './config.js';
import { state } from './state.js';
import {
  checkAuth, loadBlockTypesAPI, loadTemplatesAPI, loadWeekData,
} from './api.js';
import {
  buildGrid, buildHeader, buildColumns, updateNowLine,
  renderAllBlocks, renderCalendarEvents, renderAllDayEvents, markFocusDay,
  handleDeleteBlock, rerenderBlock, renderBlock, updateWeekSummary,
  registerGridCallbacks,
} from './grid.js';
import {
  generateWeekFromTemplates, deleteTemplate,
  openCreateModal, openCreateModalDefault, openEditModal, closeModal, saveModal,
  duplicateEditingBlock, deleteEditingBlock,
  openChoiceModal, closeChoiceModal,
  openSettings, closeSettings, addBlockTypeFromForm,
  openGCalEventModal, closeGCalModal, saveGCalModal,
  registerModalCallbacks,
} from './modals.js';
import {
  renderSidebar,
  selectBlock, deselectBlock,
  registerSidebarCallbacks,
} from './sidebar.js';
import {
  restoreRunningTimer,
  startTimer, stopTimer, registerTimerCallbacks,
} from './timer.js';
import { openDashboard, closeDashboard } from './dashboard.js';

// ══════════════════════════════════════════════════════════
// REGISTER CROSS-MODULE CALLBACKS
// ══════════════════════════════════════════════════════════
registerGridCallbacks({
  openCreateModal,
  openEditModal,
  openChoiceModal,
  openGCalEventModal,
  renderSidebar,
  selectBlock,
  deselectBlock,
  deleteTemplate,
  toast,
});

registerModalCallbacks({
  rerenderBlock,
  renderBlock,
  renderAllBlocks,
  updateWeekSummary,
  handleDeleteBlock,
  renderSidebar,
  renderCalendarEvents,
});

registerSidebarCallbacks({
  rerenderBlock,
  openEditModal,
  handleDeleteBlock,
  startTimer,
  stopTimer,
});

registerTimerCallbacks({ renderSidebar });

// ══════════════════════════════════════════════════════════
// CALENDAR VIEW (week | day)
// ══════════════════════════════════════════════════════════
function currentCalView() {
  return sanitizeCalView(localStorage.getItem(CAL_VIEW_KEY));
}

function renderCurrentWeek() {
  state.selectedTaskId = null;
  if (state.selectedBlockId) deselectBlock();
  const wk = weekKey(state.currentMonday);
  buildHeader();
  buildColumns();
  updateNowLine();
  return loadWeekData(wk).then(() => {
    generateWeekFromTemplates(wk);
    renderAllBlocks();
    renderCalendarEvents();
    renderAllDayEvents();
    buildHeader();
    renderSidebar();
  });
}

function applyCalView(view, { rerender = true } = {}) {
  const v = sanitizeCalView(view);
  localStorage.setItem(CAL_VIEW_KEY, v);
  document.body.classList.toggle('view-day', v === 'day');
  document.getElementById('view-week')?.classList.toggle('is-active', v === 'week');
  document.getElementById('view-day')?.classList.toggle('is-active', v === 'day');
  if (v === 'day') {
    // Entrant a la vista dia: avui si és la setmana visible, si no el dilluns
    state.focusDay = isCurrentWeek() ? todayIndex() : 0;
  }
  if (rerender) renderCurrentWeek();
}

function todayIndex() {
  return (new Date().getDay() + 6) % 7;
}

function isCurrentWeek() {
  return weekKey(getMonday(new Date())) === weekKey(state.currentMonday);
}

// ══════════════════════════════════════════════════════════
// WEEK NAVIGATION
// ══════════════════════════════════════════════════════════
function goWeek(delta) {
  state.currentMonday = new Date(state.currentMonday);
  state.currentMonday.setDate(state.currentMonday.getDate() + delta * 7);
  return renderCurrentWeek();
}

// Vista dia: avança/retrocedeix un dia; només recarrega si canvia de setmana.
function goDay(delta) {
  const d = new Date(state.currentMonday);
  d.setDate(d.getDate() + state.focusDay + delta);
  state.focusDay = (d.getDay() + 6) % 7;
  const monday = getMonday(d);
  if (weekKey(monday) === weekKey(state.currentMonday)) {
    markFocusDay();
    return;
  }
  state.currentMonday = monday;
  return renderCurrentWeek();
}

function goStep(delta) {
  return currentCalView() === 'day' ? goDay(delta) : goWeek(delta);
}

// ══════════════════════════════════════════════════════════
// INIT
// ══════════════════════════════════════════════════════════
async function init() {
  const authed = await checkAuth();
  if (!authed) return;

  // Fire-and-forget: ensures "Timeblocking" GCal exists
  import('./api.js').then(({ getTimeblockingCalendarAPI }) => {
    getTimeblockingCalendarAPI().catch(() => {});
  });

  await Promise.all([loadBlockTypesAPI(), loadTemplatesAPI()]);

  const wk = weekKey(state.currentMonday);
  await loadWeekData(wk);
  generateWeekFromTemplates(wk);
  buildGrid();
  renderSidebar();

  // Time tracking: restore a running timer (if any) and wire the dashboard
  restoreRunningTimer();
  document.getElementById('btn-stats').addEventListener('click', openDashboard);
  document.getElementById('dashboard-close-btn').addEventListener('click', closeDashboard);
  document.getElementById('dashboard-overlay').addEventListener('click', e => {
    if (e.target === document.getElementById('dashboard-overlay')) closeDashboard();
  });

  // Settings
  document.getElementById('btn-settings').addEventListener('click', openSettings);
  document.getElementById('settings-close-btn').addEventListener('click', closeSettings);
  document.getElementById('settings-overlay').addEventListener('click', e => {
    if (e.target === document.getElementById('settings-overlay')) closeSettings();
  });
  document.getElementById('settings-add-btn').addEventListener('click', addBlockTypeFromForm);

  // Navigation
  document.getElementById('btn-prev').addEventListener('click', () => goStep(-1));
  document.getElementById('btn-next').addEventListener('click', () => goStep(1));
  document.getElementById('btn-today').addEventListener('click', () => {
    state.focusDay = todayIndex();
    if (isCurrentWeek()) { markFocusDay(); return; }
    state.currentMonday = getMonday(new Date());
    renderCurrentWeek();
  });

  // View toggle (Setmana / Dia) — ignora clics a la vista ja activa
  document.getElementById('view-week').addEventListener('click', () => {
    if (currentCalView() !== 'week') applyCalView('week');
  });
  document.getElementById('view-day').addEventListener('click', () => {
    if (currentCalView() !== 'day') applyCalView('day');
  });

  // Block modal
  document.getElementById('btn-cancel').addEventListener('click', closeModal);
  document.getElementById('btn-save').addEventListener('click', saveModal);
  document.getElementById('btn-dup-block').addEventListener('click', duplicateEditingBlock);
  document.getElementById('btn-del-block').addEventListener('click', deleteEditingBlock);
  document.getElementById('modal-overlay').addEventListener('click', e => {
    if (e.target === document.getElementById('modal-overlay')) closeModal();
  });

  // Recurrence toggle
  document.getElementById('f-recur').addEventListener('change', e => {
    state.recurEnabled = e.target.checked;
    document.getElementById('day-pills-section').style.display = state.recurEnabled ? 'block' : 'none';
  });

  // GCal event modal
  document.getElementById('gcal-close-btn').addEventListener('click', closeGCalModal);
  document.getElementById('gcal-cancel-btn').addEventListener('click', closeGCalModal);
  document.getElementById('gcal-save-btn').addEventListener('click', saveGCalModal);
  document.getElementById('gcal-modal-overlay').addEventListener('click', e => {
    if (e.target === document.getElementById('gcal-modal-overlay')) closeGCalModal();
  });

  // Choice modal
  document.getElementById('choice-cancel').addEventListener('click', closeChoiceModal);
  document.getElementById('choice-overlay').addEventListener('click', e => {
    if (e.target === document.getElementById('choice-overlay')) closeChoiceModal();
  });

  // Start/end time sync in modal
  document.getElementById('f-start').addEventListener('change', () => {
    const s  = parseInt(document.getElementById('f-start').value);
    const en = parseInt(document.getElementById('f-end').value);
    if (en - s < MIN_DUR) document.getElementById('f-end').value = Math.min(s + MIN_DUR, TOTAL_S);
  });
  document.getElementById('f-end').addEventListener('change', () => {
    const s  = parseInt(document.getElementById('f-start').value);
    const en = parseInt(document.getElementById('f-end').value);
    if (en - s < MIN_DUR) document.getElementById('f-start').value = Math.max(en - MIN_DUR, 0);
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', e => {
    const gcalOpen  = document.getElementById('gcal-modal-overlay').classList.contains('open');
    const modalOpen = document.getElementById('modal-overlay').classList.contains('open');
    const dashOpen  = document.getElementById('dashboard-overlay').classList.contains('open');

    if (e.key === 'Escape' && dashOpen)  { closeDashboard(); return; }
    if (e.key === 'Escape' && gcalOpen)  { closeGCalModal(); return; }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && gcalOpen) { saveGCalModal(); return; }
    if (e.key === 'Escape' && modalOpen) { closeModal(); return; }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && modalOpen) { saveModal(); return; }
    if (e.key === 'n' && !modalOpen && !gcalOpen &&
        e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
      openCreateModalDefault();
    }
  });

  // Deselect when clicking empty calendar area
  document.getElementById('days-area').addEventListener('click', e => {
    if (!e.target.closest('.block') && state.selectedBlockId) deselectBlock();
  });

  // Escape also deselects block / closes task detail (capture phase, when modal is closed)
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    if (document.getElementById('modal-overlay').classList.contains('open')) return;
    if (state.selectedTaskId) { state.selectedTaskId = null; renderSidebar(); }
    else if (state.selectedBlockId) { deselectBlock(); }
  }, true);

  // Aplica la vista desada (week per defecte). buildGrid() ja s'ha executat;
  // si és 'day', força avui i re-renderitza.
  const savedView = currentCalView();
  if (savedView === 'day') {
    applyCalView('day');               // avui (setmana actual) + re-render
  } else {
    applyCalView('week', { rerender: false });
  }

  // Scroll to ~7:00 AM on load
  const wrapper = document.getElementById('calendar-wrapper');
  wrapper.scrollTop = (7 - START_H) * 4 * SLOT_H - 20;
}

init();
